import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { DropdownList } from "react-widgets";
import { toastr } from "react-redux-toastr";
import moment from "moment";
import DateInput from "../../../../components/form-group/date-input";
import TimeInput from "../../../../components/form-group/time-input";
import RemoteDataDropDown from "../../../../components/dropdown/RemoteDataDropDown";
import CheckBox from "../../../../components/check-box";
import ErrorMessage from "../../../../components/text/ErrorMessage";
import {
  Lead,
  CreateVehicle,
  VehicleModel,
  CreateShowroomVisit,
  ILeadAction,
} from "../../../../core/api/models";
import {
  createStartOfDayWithTimeZone,
  getLeadTitle,
  useLead,
  DateFormats,
  Customer,
} from "../../../../core";
import "react-widgets/dist/css/react-widgets.css";
import api from "../../../../api";
import { useMasterData } from "../../../../core/hooks/useMasterData";
import { combineDateAndTime } from "../../../../utils/CombineDateAndTime";
import AutoCompleteInput from "../../../../components/form-group/auto-complete-input";
import { ActivityModel } from "../activities/add-activity";
import { useLeadActivities } from "../../../../core/hooks/useLeadActivities";
import { ILeadEventInitData } from "../activities/lead-activity-listing";
import { ITimePeriodData } from "../../../../core/components/time-period/custom-time-period";
import {
  isTimePeriodValid,
  SingleDayTimePeriod,
} from "../../../../core/components/time-period/single-day-time-period";
import { IFormFilterData } from "../activities/edit-event-popup";
import { useAppSettings } from "../../../../core/hooks/useAppSettings";
import SalesPersonsAutoCompleteInput from "../../../../components/form-group/sales-persons-auto-complete-input";
import { UserAvatar } from "../../../../components/render-file/UserAvatar";

const ScheduleAShowroomVisit: React.FunctionComponent<{
  // leadDetails?: Lead;
  action?: ILeadAction;
  leadId?: string;
  leads?: Lead[];
  onClose?: () => void;
  onSuccess?: () => void;
  className?: string;
  activity?: ActivityModel;
  onEditSubmit?: (activity: ActivityModel) => void;
  onDateChange?: (date: Date) => void;
  isEdit?: boolean;
  onTimePeriodChange?: (data?: ITimePeriodData) => void;
  onDataChange?: (data: IFormFilterData) => void;
  excludedDates?: Date[];
}> = (props) => {
  const appSettings = useAppSettings(api.settings);
  const { scheduleShowroomVisit, updating, getLead } = useLead(api.lead);
  const isActivityEditing = props.isEdit ?? false;
  const { control, handleSubmit, errors, reset, watch, setValue } = useForm<
    CreateShowroomVisit & { leadId: string }
  >({
    reValidateMode: "onChange",
    mode: "onSubmit",
  });

  const [isSubmiting, setIsSubmiting] = React.useState<boolean>(false);

  const [isSalesPersonChanging, setSalesPersonChanging] = useState(false);

  const [selectedCustomers, setSelectedCustomers] = React.useState<Customer[]>(
    props.activity?.users || []
  );
  const [userReset, setUserReset] = React.useState<boolean>(false);
  const [users, setUsers] = React.useState<Customer[] | undefined>(
    !!props?.leads?.length ? props?.leads?.[0]?.corporateUsers : []
  );
  const loadShowroom = async () => {
    const response = await api.settings.getShowrooms({ ecommerceOnly: true });
    return response;
  };

  const isObject = (value?: any) => {
    if (value != null && value.hasOwnProperty("_id")) {
      return true;
    }
    return false;
  };

  React.useEffect(() => {
    if (props.leadId) {
      loadLead(props.leadId);
    }
  }, [props.leadId]);

  const loadLead = async (leadId: string) => {
    const response = await api.lead.getLead(leadId);
    setUsers(response?.data?.corporateUsers);
  };

  const showroom = isObject(watch("showroom"))
    ? (watch("showroom") as any)._id
    : watch("showroom");

  React.useEffect(() => {
    if (props.activity?.showRoom && !showroom) {
      setValue("showroom", props.activity?.showRoom?._id);
    }
  }, [props.activity?.showRoom]);

  const handleDataChange = () => {
    if (props.onDataChange) {
      const salesperson: any = watch("salesPerson");
      const showroomData: any = watch("showroom");
      const data: IFormFilterData = {
        salesPerson: isObject(salesperson) ? salesperson._id : salesperson,
        showRoom: isObject(showroomData) ? showroomData._id : showroomData,
      };
      props.onDataChange(data);
    }
  };
  const {
    getAllLeadActivities: getLeadActivities,
    getLeadActivitiesAll,
  } = useLeadActivities(api.lead);
  const onSubmit = async (data: CreateShowroomVisit & { leadId: string }) => {
    if (!isActivityEditing) {
      setIsSubmiting(true);
      const updateData: CreateShowroomVisit = {
        actionId: props?.action?._id!,
        showroom: isObject(data?.showroom)
          ? (data.showroom as any)._id
          : data.showroom,
        salesPerson: isObject(data.salesPerson)
          ? (data.salesPerson as any)._id
          : data.salesPerson,
        scheduleStartDate: data?.scheduleStartDate,
        note: data?.note,
        busyEvent: data?.busyEvent,
        users: data.users,
      };

      if (data?.timePeriod?.startDate && data?.timePeriod?.startTime) {
        updateData.scheduleStartDate = combineDateAndTime(
          createStartOfDayWithTimeZone(data?.timePeriod?.startDate),
          data?.timePeriod.startTime
        );
      }

      if (data?.timePeriod?.startDate && data?.timePeriod?.endTime) {
        updateData.scheduleEndDate = combineDateAndTime(
          createStartOfDayWithTimeZone(data?.timePeriod?.startDate),
          data?.timePeriod?.endTime
        );
      }
      scheduleShowroomVisit(updateData, data.leadId, () => {
        toastr.success("Success", "Showroom visit added successfully");
        if (props.leadId) {
          getLeadActivities(props.leadId);
          getLead(props.leadId);
        }
        const leadIds = props.leads?.map((x) => x._id as string);
        if (leadIds && leadIds.length) {
          getLeadActivitiesAll(leadIds || []);
        }
        setIsSubmiting(false);
        reset({
          showroom: "",
          salesPerson: "",
          scheduleStartDate: "",
          scheduleEndDate: "",
          note: "",
          startTime: "",
          endTime: "",
        });
        props.onSuccess?.();
      });
    } else {
      if (activity != null) {
        const editedActivity: ActivityModel & { contactUserIds?: string[] } = {
          type: activity.type,
          title: activity.title,
          timePeriod: data.timePeriod,
          salesPerson: (isObject(data.salesPerson)
            ? data.salesPerson
            : { _id: data.salesPerson, name: "" }) as any,
          leadId: props.leadId as any,
          note: data.note as any,
          showRoom: (isObject(data.showroom)
            ? data.showroom
            : { _id: data.showroom!, title: "" }) as any,
          busyEvent: data.busyEvent,
          contactUserIds: data.users,
        };
        if (props.onEditSubmit) {
          props.onEditSubmit(editedActivity);
        }
      }
    }
  };

  if (!props.leadId && !props.leads?.length) {
    return <></>;
  }

  const renderUsers = () => {
    return (
      <div
        key={props.leadId ?? "" + "showroom-visit-salespereson"}
        className="form-group font-weight-semibold mb-3"
      >
        <label htmlFor="" className="font-sm text-uppercase">
          Users<span className="text-danger">*</span>
        </label>
        <div className="mb-2 sa-custom-dropdown">
          <Controller
            control={control}
            rules={{ required: true }}
            name="users"
            defaultValue={activity?.users}
            render={({ onChange, value, name }) => (
              <AutoCompleteInput
                defaultValue={selectedCustomers[0]}
                removeIds={selectedCustomers?.map((x) => x._id || "") || []}
                placeholder={"Users"}
                onChange={(val) => {
                  const customers = !!selectedCustomers?.length
                    ? [...selectedCustomers]
                    : [];
                  customers.push(val);
                  setSelectedCustomers(customers);
                  setValue(
                    "users",
                    customers?.map((x) => x._id)
                  );
                  setUserReset(false);
                }}
                onReset={() => {
                  onChange([]);
                  setSelectedCustomers([]);
                }}
                apiUrl=""
                // dataLoader={(query?: string) => {
                //   return api.company.searchCustomer(
                //     query || '',
                //     props.companyId || ''
                //   );
                // }}
                options={users}
                valueField={"name"}
                userReset={userReset}
              />
            )}
          />
        </div>
        {selectedCustomers?.map((customerDetails) => {
          return (
            <div className="px-2 py-1 bg-gray-100 border d-flex align-items-center mb-2">
              <div
                className={`avatar-progress avatar-progress--xs position-relative cursor-pointer ml-1 mr-2 ${
                  customerDetails ? "" : "avatar-progress--add"
                }`}
              >
                <svg>
                  <circle r="12" cx="50%" cy="50%" className="default"></circle>
                  <circle
                    r="12"
                    cx="50%"
                    cy="50%"
                    className="progress"
                    stroke-dasharray="117"
                    stroke-dashoffset="45"
                  ></circle>
                </svg>

                <UserAvatar
                  id={customerDetails?._id}
                  className="position-absolute left-0 right-0 top-0 bottom-0 m-auto rounded-circle"
                  alt=""
                  title={customerDetails?.name}
                  imageUrl={customerDetails?.photo?.location}
                  image={customerDetails?.photo}
                ></UserAvatar>
              </div>
              <div className="flex-fill text-truncate custom-dropdown__value-text font-normal font-weight-semibold pl-1">
                {customerDetails?.fullName || customerDetails?.name}
              </div>

              <i
                className="icon-close ml-auto font-xxs font-weight-bold"
                onClick={() => {
                  const customers = selectedCustomers?.filter(
                    (x) => x._id?.toString() !== customerDetails._id?.toString()
                  );

                  setSelectedCustomers(customers);
                  setValue(
                    "users",
                    customers?.map((x) => x._id)
                  );
                  setUserReset(true);
                }}
              ></i>
            </div>
          );
        })}
        <Controller
          control={control}
          rules={{ required: "Lead is Required" }}
          name="users"
          // defaultValue={props.leadId}
          render={() => <></>}
        />
        {errors.users && <ErrorMessage>Users is required</ErrorMessage>}
      </div>
    );
  };
  const renderLeadOption = () => {
    if (props.leadId) {
      return (
        <Controller
          control={control}
          rules={{ required: "Lead is Required" }}
          name="leadId"
          defaultValue={props.leadId}
          render={() => <></>}
        />
      );
    }
    return (
      <div className="form-group font-weight-semibold mb-3">
        <label htmlFor="" className="font-sm text-uppercase">
          Lead<span className="text-danger">*</span>
        </label>
        <Controller
          control={control}
          rules={{ required: "Lead is required" }}
          name="leadId"
          defaultValue={props.leads?.[0]._id}
          render={({ onChange, value, name }) => (
            <DropdownList
              data={props.leads?.map((lead) => ({
                _id: lead._id,
                title: `${getLeadTitle(lead)} - Created on ${moment(
                  lead.createdAt
                ).format(DateFormats.NormalDateTimeFormat)} By ${
                  lead?.createdBy?.name || "Alghanim website"
                }`,
              }))}
              textField="title"
              value={value}
              defaultValue={value}
              valueField="_id"
              onChange={(val) => {
                onChange(val._id);
                setValue("showroom", val?.showroom?._id || undefined);
                setValue(
                  "salesPerson",
                  val?.currentSalesPerson?._id || undefined
                );
              }}
              placeholder="Select Lead"
            ></DropdownList>
          )}
        />
        <div>
          <ErrorMessage>{errors.leadId?.message}</ErrorMessage>
        </div>
      </div>
    );
  };
  const activity = props.activity;

  let minDate: any;
  if (
    appSettings.data?.leadActivityAssignment
      ?.noOfDaysToAllowBackdatedActivity !== undefined &&
    appSettings.data?.leadActivityAssignment
      ?.noOfDaysToAllowBackdatedActivity >= 0
  ) {
    minDate = new Date().setDate(
      new Date().getDate() -
        appSettings.data?.leadActivityAssignment
          ?.noOfDaysToAllowBackdatedActivity
    );
  }

  return (
    <div className={`${props.className || ""} pt-2 pb-4`}>
      <form onSubmit={handleSubmit(onSubmit)}>
        {renderLeadOption()}
        {(users?.length || !!activity?.users?.length) && renderUsers()}
        <div className="form-group font-weight-semibold mb-3">
          <label htmlFor="" className="font-sm text-uppercase">
            Office<span className="text-danger">*</span>
          </label>
          <Controller
            control={control}
            rules={{ required: true }}
            name="showroom"
            defaultValue={activity?.showRoom}
            render={({ onChange, value, name }) => (
              <RemoteDataDropDown
                loadData={loadShowroom}
                textField="title"
                value={value}
                defaultValue={value}
                valueField="_id"
                onChange={(val) => {
                  onChange(val._id);
                  setValue("salesPerson", undefined);
                  handleDataChange();
                }}
                placeholder="Select Office"
              ></RemoteDataDropDown>
            )}
          />
          {errors.showroom && (
            <div>
              <ErrorMessage>Office is required</ErrorMessage>
            </div>
          )}
        </div>
        <div className="form-group font-weight-semibold mb-3">
          <label htmlFor="" className="font-sm text-uppercase">
            Date and Time<span className="text-danger">*</span>
          </label>
          <div key={JSON.stringify(activity?.timePeriod ?? {})}>
            <Controller
              control={control}
              rules={{ required: true }}
              name="timePeriod"
              defaultValue={activity?.timePeriod}
              render={({ onChange, value, name }) => (
                <SingleDayTimePeriod
                  onChange={(val: ITimePeriodData) => {
                    if (isTimePeriodValid(val)) {
                      onChange(val);
                      if (props.onTimePeriodChange) {
                        props.onTimePeriodChange(val);
                      }
                    } else {
                      onChange(undefined);
                      if (props.onTimePeriodChange) {
                        props.onTimePeriodChange(undefined);
                      }
                    }
                  }}
                  defaultValue={activity?.timePeriod}
                  minDate={minDate}
                  appointmentTime={
                    appSettings?.data?.application?.appointmentTime
                  }
                  excludeDates={props.excludedDates?.map(
                    (date) => new Date(date)
                  )}
                  useMinimumDuration
                ></SingleDayTimePeriod>
              )}
            />
          </div>
          {errors.timePeriod && (
            <ErrorMessage>Please enter a valid time period</ErrorMessage>
          )}
        </div>

        <div
          key={activity?.salesPerson?._id ?? "" + "showroom-visit-salespereson"}
          className="form-group font-weight-semibold mb-3"
        >
          <label htmlFor="" className="font-sm text-uppercase">
            Transaction Manager<span className="text-danger">*</span>
          </label>
          <Controller
            control={control}
            rules={{ required: true }}
            name="salesPerson"
            defaultValue={activity?.salesPerson}
            render={({ onChange, value, name }) => (
              <SalesPersonsAutoCompleteInput
                loadingText={isSalesPersonChanging ? "Assigning ..." : ""}
                defaultValue={value}
                placeholder={"Transaction Manager"}
                onChange={(val) => {
                  onChange(val._id);
                  handleDataChange();
                }}
                apiUrl="api/user/sales-persons"
                valueField={"name"}
                dataLoader={(query?: string) => {
                  return api.customer.searchSalesPerson(
                    {
                      query,
                      showroomId: showroom,
                      hideBlocked: true,
                    },
                    "lead-assignees"
                  );
                }}
                disabled={updating}
                hideBlocked={true}
              />
              // <AutoCompleteInput
              //   defaultValue={value}
              //   placeholder={'Agent'}
              //   onChange={(val) => {
              //     onChange(val._id);
              //     handleDataChange();
              //     if (!showroom) {
              //       setValue('showroom', val.showroomIds?.[0]);
              //     }
              //   }}
              //   onReset={() => {
              //     onChange(undefined);
              //     handleDataChange();
              //   }}
              //   apiUrl="api/user/sales-persons"
              //   dataLoader={(query?: string) => {
              //     return api.customer.searchSalesPerson(
              //       {
              //         query,
              //         showroomId: showroom,
              //         hideBlocked: true,
              //       },
              //       'showroom-visits'
              //     );
              //   }}
              //   valueField={'name'}
              // />
            )}
          />
          {errors.salesPerson && (
            <ErrorMessage>Transaction Manager is required</ErrorMessage>
          )}
        </div>
        <div className="form-group font-weight-semibold mb-3">
          <label htmlFor="" className="font-sm text-uppercase">
            TYPE
          </label>
          <Controller
            control={control}
            name="busyEvent"
            defaultValue={activity?.busyEvent || true}
            render={({ onChange, value, name }) => (
              <DropdownList
                value={value}
                data={[
                  { label: "Busy", value: true },
                  // { label: 'Free', value: false },
                ]}
                textField={"label"}
                valueField={"value"}
                placeholder={"Select Type"}
                onChange={(val) => {
                  onChange(val.value);
                }}
                disabled
              />
            )}
          />
        </div>
        <div className="form-group font-weight-semibold mb-3">
          <label htmlFor="" className="font-sm text-uppercase">
            Note
          </label>
          <Controller
            control={control}
            // rules={{ required: true }}
            name="note"
            defaultValue={activity?.note}
            render={({ onChange, value, name }) => (
              <textarea
                className="form-control rounded-sm font-weight-semibold h-auto"
                placeholder="Take a note"
                name={name}
                value={value}
                onChange={onChange}
                id="note"
              />
            )}
          />
          {errors.note && <ErrorMessage>Note is required</ErrorMessage>}
        </div>

        <div className="form-group font-weight-semibold mb-3 text-right pt-3 justify-content-between align-items-center">
          <div className="text-right">
            <button
              onClick={props.onClose}
              className="btn btn-outline-dark py-2 font-weight-semibold font-normal px-4 mr-1"
            >
              Cancel
            </button>
            <button
              disabled={updating}
              className="btn btn-primary py-2 font-weight-semibold font-normal px-4 "
            >
              Submit
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ScheduleAShowroomVisit;
